import type { PublicCampaignSchema, WidgetVariant } from "@/types";

type Subtype = PublicCampaignSchema["subtype"];

/**
 * Tournament subtypes with no FE implementation yet — backend support ships ahead of the
 * widget UI (one subtype per stacked branch/MR), so `assembleTournaments` hides any campaign
 * whose subtype is listed here rather than rendering an unstyled/broken tile.
 *
 * FUTURE AGENTS: when you implement the FE for one of these subtypes, remove its entry from
 * this set (and delete the set entirely once it's empty) — that is the safeguard this comment
 * is flagging for removal, not a permanent exclusion.
 */
const UNSUPPORTED_TOURNAMENT_SUBTYPES = new Set<Subtype>([
  "RA", // RACES — remove once RA's FE lands
  "TM", // TOURNAMENT_MISSION — remove once TM's FE lands
  // "IM" (the chase) is served by `modules/chase` since #4041, not hidden here.
]);

/**
 * Subtypes one surface already draws while the others still hide them. Races: the overlay's
 * dock panel shows a race's standing and spins left (#4012), as the legacy overlay did, and the
 * on-site Campaign Dashboard renders them through the tournament views (Race badge, spin cap,
 * spins left); in-game keeps hiding races until they are brought to that surface. When every
 * surface supports a subtype, drop it from the set above and from here.
 */
const SURFACE_SUPPORTED_SUBTYPES: Partial<Record<WidgetVariant, Subtype[]>> = {
  overlay: ["RA"],
  "on-site": ["RA"],
};

/** Whether `assembleTournaments` hides a campaign of `subtype` on `variant`. */
export const isTournamentSubtypeHidden = (
  subtype: Subtype,
  variant: WidgetVariant
): boolean =>
  UNSUPPORTED_TOURNAMENT_SUBTYPES.has(subtype) &&
  !SURFACE_SUPPORTED_SUBTYPES[variant]?.includes(subtype);
